/* eslint-disable @typescript-eslint/no-unused-vars */
import LegislativeEventsSearchParams from '@enview/interface/events/LegislativeEventsSearchParams';
import { Hearing } from '@enview/interface/types/bills/Bill';
import { HearingsFilterPreference } from '@enview/interface/types/OrganizationUser';
import { faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  DateParam,
  DelimitedArrayParam,
  NumberParam,
  StringParam,
  decodeDate,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { JurisdictionAPI, TeamAPI } from '../../../api';
import {
  getDefaultPage,
  getPageForQuery,
  SortableDataTableColumn,
} from '../../../helpers/TableHelper';
import JurisdictionHearingStatusBadge from '../JurisdictionHearingStatusBadge';
import JurisdictionHearingsFilters from './JurisdictionHearingsFilters';
import { State, Thunk } from '../../../dux/@types';
import { useDispatch, useSelector } from 'react-redux';
import { checkPermission, getOrgForTeamMode, getOrgIdForTeamMode } from '../../../dux';
import { g as abilityGlossary } from '../../../config/ability';
import { SortBy, SortByParam, SortOrder } from '../../../models/SortOrder';
import { isEqual } from 'lodash-es';
import { OptionType } from '../../Shared/DropDowns/SingleSelectDropDown';
import { AdvFilterValue } from '@enview/interface/enum';
import filteredTeamMembershipsForSelector from '../../../helpers/TeamHelper';
import HearingRelatedBills from './HearingRelatedBills';
import Info from '../../../components/svg/InfoCircleIcon';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { CURRENT_SESSION_FILTER } from '../../../api/jurisdiction';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { filterJurisdictionsByTeamMode } from '../../../helpers/JurisdictionSelectionHelper';
import GatedFullScreenTakeover from '../../Shared/Overlays/GatedFullScreenTakeover';
import usePageFilterPreferences, {
  getJurisdictionsToSave,
  getRestoredJurisdictions,
} from '../../../hooks/usePageFilterPreferences';

type JurisdictionHearingsProps = {
  jurisAbbr?: string;
};

const JurisdictionHearings: React.FC<JurisdictionHearingsProps> = (props) => {
  const { jurisAbbr = '' } = props;
  const showSingleJurisdiction = !!jurisAbbr;

  const [sort, setSort] = useState<SortOrder>(
    new SortOrder(SortBy.RECENT_ACTIVITY, false),
  );

  const teamModeOrg = useSelector(getOrgForTeamMode);

  const organizationUser = useSelector(
    (state: State) => state.account.organizationUser,
  );

  const { data: supportedJurisdictions, isLoading: isLoadingJurisdictions } =
    JurisdictionAPI.endpoints.getUserSupportedJurisdictions.useQuery(
      organizationUser?.id ?? skipToken,
    );
  const teamJurisdictions = filterJurisdictionsByTeamMode(
    supportedJurisdictions,
    teamModeOrg,
  );

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, 10),
    offset: withDefault(NumberParam, 0),
    startDateRange: withDefault(DateParam, undefined),
    endDateRange: withDefault(DateParam, undefined),
    orderColumn: withDefault(StringParam, SortByParam.LATEST),
    orderDirection: withDefault(StringParam, 'DESC'),
    cmteNames: withDefault(StringParam, undefined),
    workspaceFilterMode: withDefault(StringParam, undefined),
    workspaces: withDefault(DelimitedArrayParam, undefined),
    sessions: withDefault(DelimitedArrayParam, [CURRENT_SESSION_FILTER]),
    jurisdictions: withDefault(
      DelimitedArrayParam,
      showSingleJurisdiction
        ? [jurisAbbr]
        : teamJurisdictions.map((j) => j.abbreviation),
    ),
  });

  const dispatch = useDispatch();
  const orgId = useSelector(getOrgIdForTeamMode);

  const canViewHearings = (): Thunk<boolean> => {
    return dispatch(
      checkPermission(abilityGlossary.VIEW, abilityGlossary.HEARINGS, true),
    );
  };

  const getFilteredJurisdictions = (): string[] => {
    if (showSingleJurisdiction) {
      return [jurisAbbr.toLowerCase()];
    }

    if (query?.jurisdictions?.length > 0) {
      const teamAbbreviations = teamJurisdictions.map((tj) =>
        tj.abbreviation.toLowerCase(),
      );
      return query.jurisdictions.filter(
        (j) => typeof j === 'string' && teamAbbreviations.includes(j.toLowerCase()),
      ) as string[];
    }

    return teamJurisdictions.map((j) => j.abbreviation);
  };

  const filteredJurisdictionSelection = getFilteredJurisdictions();

  const params: LegislativeEventsSearchParams = {
    ...query,
    sessions: query.sessions?.filter((s) => typeof s === 'string') as string[],
    workspaces: query.workspaces?.filter((w) => typeof w === 'string') as string[],
    workspaceFilterMode: query.workspaceFilterMode as AdvFilterValue,
    orderDirection: query.orderDirection as 'ASC' | 'DESC',
    cmteNames: undefined,
    jurisdictions: filteredJurisdictionSelection,
  };

  params.cmteNames = query.cmteNames;

  const { data: teamMemberships, isLoading: isLoadingTeamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.useQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const teamModeOrgId = useSelector(getOrgIdForTeamMode);

  const teams = useMemo(
    () => filteredTeamMembershipsForSelector(teamMemberships, orgUser, teamModeOrgId),
    [teamMemberships, orgUser, teamModeOrgId],
  );

  const teamsOptions = useMemo(() => {
    const options: OptionType<number>[] = teams.map((team) => ({
      label: team.name,
      value: team.id,
    }));

    return options;
  }, [teams, orgId]);

  const teamAbbreviations = teamJurisdictions.map((j) => j.abbreviation);

  const applySavedFilters = (filters?: HearingsFilterPreference): void => {
    // Workspaces the user has left would still filter results without
    // appearing as selected in the workspace dropdown
    const workspaces = filters?.workspaces?.filter((id) =>
      teamsOptions.some((option) => String(option.value) === id),
    );
    const hasWorkspaces = !!workspaces?.length;
    setQuery(
      {
        jurisdictions: getRestoredJurisdictions(
          filters?.jurisdictions,
          teamAbbreviations,
        ),
        sessions: filters?.sessions,
        cmteNames: filters?.cmteNames,
        workspaces: hasWorkspaces ? workspaces : undefined,
        workspaceFilterMode: hasWorkspaces ? filters?.workspaceFilterMode : undefined,
        startDateRange: decodeDate(filters?.startDateRange) ?? undefined,
        endDateRange: decodeDate(filters?.endDateRange) ?? undefined,
        offset: undefined,
      },
      'replaceIn',
    );
  };

  const { isHydrated, filtersKey, saveFilters } = usePageFilterPreferences({
    page: 'hearings',
    enabled: !showSingleJurisdiction,
    isReady: !isLoadingJurisdictions && !isLoadingTeamMemberships,
    pageParamKeys: Object.keys(query),
    applyFilters: applySavedFilters,
  });

  const saveHearingsFilters = (filters: HearingsFilterPreference): void => {
    saveFilters({
      ...filters,
      jurisdictions: getJurisdictionsToSave(
        filters.jurisdictions ?? [],
        teamAbbreviations,
      ),
    });
  };

  // Wait for saved filters and the user's jurisdictions so the first search uses
  // the final params instead of issuing a request that is immediately replaced.
  const canSearch = isHydrated && (showSingleJurisdiction || !isLoadingJurisdictions);
  const {
    data: hearings,
    isLoading,
    isFetching,
  } = JurisdictionAPI.endpoints.getEvents.useQuery(canSearch ? params : skipToken);

  const { t } = useTranslation();

  const ref = useRef<HTMLDivElement>(null);

  const resetScroll = (): void => {
    if (ref && ref.current) {
      ref.current.scrollIntoView();
    }
  };

  const [tooltipAnchor, setTooltipAnchor] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);

  const renderDate = (hearing: Hearing) => {
    const startDateTime = new Date(hearing.start);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    };

    const formattedDate = startDateTime.toLocaleDateString('en-US', options);
    const tooltipId = `hearings-table-tooltip-${hearing.id}`;

    const handleDateInfoClick = () => {
      setTooltipAnchor(tooltipId);
      setShowTooltip(true);
    };

    return (
      <p className="hearings-date-cell" id={tooltipId}>
        <span>{formattedDate}</span>
        <Info
          className="ml-1"
          color="#4a4a4a"
          onClick={handleDateInfoClick}
          size={16}
        />
      </p>
    );
  };

  const columns: SortableDataTableColumn[] = useMemo(() => {
    const dtColumns = [
      {
        name: t('hearings.table.status'),
        selector: 'status',
        sortable: true,
        sortKey: SortBy.HEARING_STATUS,
        maxWidth: '160px',
        style: {
          padding: '0px 0px 0px 10px',
        },
        cell: (hearing: Hearing) => (
          <JurisdictionHearingStatusBadge hearing={hearing} />
        ),
        minWidth: '125px',
      },
      {
        name: t('hearings.table.committee'),
        cell: (hearing: Hearing) => {
          const links = hearing.relatedCommittees?.map((committee) => (
            <Link
              className="table-link"
              key={`committee-table-link-${committee.id}`}
              to={`/committee/${committee.id}`}
            >
              {committee.name}
            </Link>
          ));
          return (
            <div className="committee-link-container">
              {links?.length ? links : hearing.eventData.name}
            </div>
          );
        },
      },
      {
        name: t('hearings.table.date'),
        sortKey: SortBy.RECENT_ACTIVITY,
        cell: (hearing: Hearing) => renderDate(hearing),
        selector: 'start',
        sortable: true,
      },
      {
        name: t('hearings.table.location'),
        ignoreRowClick: true,
        cell: (hearing: Hearing) => hearing.eventData?.location?.name ?? '',
      },
      {
        name: t('hearings.table.bill'),
        style: {
          paddingLeft: '0px',
        },
        minWidth: '110px',
        ignoreRowClick: true,
        selector: 'related-bills',
        cell: (hearing: Hearing) => <HearingRelatedBills hearing={hearing} />,
      },
      {
        name: t('hearings.table.description'),
        ignoreRowClick: true,
        minWidth: '200px',
        cell: (hearing: Hearing) => (
          <div className="truncate-box">{hearing.eventData?.description ?? ''}</div>
        ),
      },
      {
        name: t('hearings.table.source'),
        ignoreRowClick: true,
        cell: (hearing: Hearing) => {
          if (!hearing.eventData.sources?.length) return '';
          const sourceUrl = hearing.eventData.sources[0].url;
          return (
            <a className="source-link" href={sourceUrl} target="_blank">
              {t('hearings.table.sourceLink')}
            </a>
          );
        },
      },
    ];

    if (!showSingleJurisdiction) {
      dtColumns.splice(1, 0, {
        name: 'State',
        selector: 'state',
        sortable: true,
        sortKey: SortBy.ALPHABETICAL,
        maxWidth: '160px',
        style: {
          padding: '0px 0px 0px 10px',
        },
        cell: (hearing: Hearing) => <p>{hearing.eventData.jurisdiction?.name}</p>,
        minWidth: '125px',
      });
    }

    return dtColumns;
  }, []);

  const getDefaultSort = useCallback((): {
    selector: string;
    direction: 'asc' | 'desc';
  } => {
    const sorderOrderKey = sort?.sortBy ?? SortBy.RECENT_ACTIVITY;
    const selector =
      columns
        .find((column) => column.sortKey === sorderOrderKey)
        ?.selector?.toString() ?? 'action_date';
    return { selector, direction: sort?.isReversed ? 'desc' : 'asc' };
  }, [sort, columns]);

  const handleSortChange = useCallback(
    (column: SortableDataTableColumn, sortDirection: 'desc' | 'asc') => {
      const sortColumn = column.sortKey ?? SortBy.RECENT_ACTIVITY;
      const sortOrder = new SortOrder(sortColumn, sortDirection === 'desc');
      if (isEqual(sort, sortOrder)) return;
      setSort(sortOrder);
      setQuery({
        orderColumn: sortOrder.getQueryParam(),
      });
    },
    [sort, setQuery],
  );

  const handlePageChange = useCallback((page: number) => {
    setQuery(getPageForQuery(page, query));
  }, []);

  const handlePageSizeChange = useCallback((currentRowsPerPage: number) => {
    setQuery({ limit: currentRowsPerPage, offset: 0 });
  }, []);

  const customStyles = {
    cells: {
      style: {
        display: 'flex',
        alignItems: 'flex-start',
      },
    },
  };

  return (
    <div className="container-fluid jurisdiction-hearings p-0">
      {canViewHearings() ? (
        <>
          <div className="jurisdiction-hearings-header">
            <div className="row m-3">
              <h2>Hearings</h2>
            </div>
          </div>
          <div>
            {isHydrated && (
              <JurisdictionHearingsFilters
                jurisAbbr={jurisAbbr}
                key={filtersKey}
                onFiltersApplied={
                  showSingleJurisdiction ? undefined : saveHearingsFilters
                }
                query={query}
                resetScroll={resetScroll}
                setQuery={setQuery}
                workspaces={teamsOptions}
              />
            )}
            <div className="table hearings-table">
              {(!canSearch || isFetching) && (
                <div className="table-spinner-overlay">
                  <Spinner animation="border" role="status" />
                </div>
              )}
              <p className="hearings-count">{hearings?.count || 0} Results</p>
              <ReactTooltip
                anchorId={tooltipAnchor}
                children={t('activity.seeSourceMessage')}
                className="tooltip"
                clickable
                delayHide={100}
                isOpen={showTooltip}
                place="bottom"
                setIsOpen={setShowTooltip}
                style={{ maxWidth: '300px' }}
              />
              <DataTable
                columns={columns}
                customStyles={customStyles}
                data={hearings?.data ?? []}
                defaultSortAsc={getDefaultSort().direction === 'asc'}
                defaultSortField={getDefaultSort().selector}
                disabled={isLoading}
                noContextMenu
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePageSizeChange}
                onSort={handleSortChange}
                pagination
                paginationDefaultPage={getDefaultPage(query)}
                paginationPerPage={query.limit}
                paginationRowsPerPageOptions={[10, 20, 50]}
                paginationServer
                paginationTotalRows={hearings?.count}
                responsive
                sortIcon={<FontAwesomeIcon icon={faCaretUp} />}
                sortServer
                striped
              />
            </div>
          </div>
        </>
      ) : (
        <GatedFullScreenTakeover
          modalBodyText={t('hearings.gatedModal.bodyText')}
          modalButton={
            <a href={t('hearings.gatedModal.link')}>
              <button className="btn btn-primary" type="button">
                {t('hearings.gatedModal.buttonText')}
              </button>
            </a>
          }
          modalHeader={t('hearings.gatedModal.headerText')}
        />
      )}
    </div>
  );
};

export default JurisdictionHearings;
